import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Newspaper, ChartLine, CloudRain, Bug, Books } from "@phosphor-icons/react";

/**
 * LivePulseSidebar — dynamic context strip that lives next to the static
 * crop encyclopedia article.
 *
 * Sections (each backed by a `/api/farmers/pulse/{slug}/{section}` GET;
 * empty state is a graceful "coming soon" tile so the page never breaks
 * before backend hydration ships).
 *
 *   • Latest News     — /pulse/{slug}/news
 *   • Prices          — /pulse/{slug}/prices
 *   • Weather note    — /pulse/{slug}/weather
 *   • Pest alerts     — /pulse/{slug}/pests
 *   • Research links  — /pulse/{slug}/research
 */
const SECTIONS = [
  { key: "news",     icon: Newspaper,  label: "Latest news",   endpoint: "news",     empty: "News pulse coming soon." },
  { key: "prices",   icon: ChartLine,  label: "Market prices", endpoint: "prices",   empty: "Live prices will surface here." },
  { key: "weather",  icon: CloudRain,  label: "Weather",       endpoint: "weather",  empty: "Local weather insight coming soon." },
  { key: "pests",    icon: Bug,        label: "Pest alerts",   endpoint: "pests",    empty: "Nearby pest signals will land here." },
  { key: "research", icon: Books,      label: "Research",      endpoint: "research", empty: "Fresh papers coming soon." },
];

export default function LivePulseSidebar({ slug }) {
  const [data, setData] = useState({});

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    // Fetch all sections in parallel. Individual failures fall through to
    // the "coming soon" empty state without breaking the page.
    Promise.all(
      SECTIONS.map((s) =>
        http.get(`/farmers/pulse/${slug}/${s.endpoint}`)
          .then(({ data }) => [s.key, data?.items || []])
          .catch(() => [s.key, null])
      )
    ).then((rows) => {
      if (!alive) return;
      setData(Object.fromEntries(rows));
    });
    return () => { alive = false; };
  }, [slug]);

  return (
    <aside
      className="rounded-2xl border border-black/10 bg-white/85 backdrop-blur p-5 md:p-6 sticky top-6 space-y-5"
      data-testid="live-pulse-sidebar"
    >
      <header>
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          Live Pulse
        </div>
        <h3 className="mt-1 font-serif-display text-2xl leading-tight">
          What's happening now
        </h3>
        <p className="mt-1 text-xs text-[#3A3830]">
          A dynamic feed of news, prices, weather, pest alerts and research
          around this crop. Updated continuously.
        </p>
      </header>

      {SECTIONS.map(({ key, icon: Icon, label, empty }) => {
        const items = data[key];
        return (
          <section key={key} data-testid={`live-pulse-${key}`} className="border-t border-black/10 pt-4">
            <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#1B4332]">
              <Icon size={13} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
              {label}
            </div>
            {items && items.length > 0 ? (
              <ul className="mt-2 space-y-2">
                {items.slice(0, 4).map((it, i) => (
                  <li key={i} className="text-sm leading-snug">
                    {it.url ? (
                      <a
                        href={it.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[color:var(--np-brand,#B85F2A)] hover:underline"
                      >
                        {it.title}
                      </a>
                    ) : (
                      <span className="text-[#241209]">{it.title}</span>
                    )}
                    {it.meta && (
                      <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F] mt-0.5">
                        {it.meta}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-2 text-xs text-[#8E8B7F] italic">{empty}</div>
            )}
          </section>
        );
      })}

      <footer className="border-t border-black/10 pt-4 text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F]">
        NaturePulse · Live Pulse (stub)
      </footer>
    </aside>
  );
}
